// Telemetry helpers for the Reli-Birds cheat-hardening initiative. These emit
// New Relic PageActions that feed the confidence-score dashboards:
//   * gameDeviceFingerprint — one per game-start, static environment snapshot
//   * jumpBird (enriched)    — one per jump, pointer + interval details
//   * gameSummary            — one per game-end, aggregated gameplay stats
//
// All attributes are string/number/boolean (NR PageAction requirement).

type PageActionPayload = Record<string, string | number | boolean | null>;

type PointerLike = {
  pointerType?: string;
  clientX?: number;
  clientY?: number;
  pressure?: number;
  tiltX?: number;
  tiltY?: number;
  isPrimary?: boolean;
};

let jumpTimestamps: number[] = [];
let visibilityChangeCount = 0;
let focusChangeCount = 0;
let clickOutOfBoundsCount = 0;
let subHumanReactionCount = 0;
let visibilityHandler: (() => void) | null = null;
let focusHandler: (() => void) | null = null;
let blurHandler: (() => void) | null = null;

// Mousemove tracking — bots using page.evaluate never generate DOM pointermove
let mouseMoveCount = 0;
let movesSinceLastJump = 0;
let movesBetweenJumpsAccum: number[] = [];
let mouseMoveHandler: (() => void) | null = null;
let mouseMoveTarget: HTMLElement | null = null;

// Pointer-bird spatial coherence — humans click near where the bird is
let clickBirdDeltaAccum: number[] = [];

// Primary pointer type — 'touch' users legitimately have 0 pointermove events
let primaryPointerType = 'unknown';

// Fingerprint snapshot — captured at game start, echoed into gameSummary
let fingerprintSnapshot: { webdriver: boolean; connRtt: number; pluginsLength: number; mimeTypesLength: number } = {
  webdriver: false,
  connRtt: -1,
  pluginsLength: -1,
  mimeTypesLength: -1,
};

function getNewRelic(): any | null {
  try {
    const nr = (window as any).newrelic;
    if (nr && typeof nr.addPageAction === 'function') return nr;
  } catch (e) {
    /* noop */
  }
  return null;
}

function safeNum(v: unknown, fallback = -1): number {
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? n : fallback;
}

function safeStr(v: unknown, fallback = ''): string {
  return typeof v === 'string' ? v : fallback;
}

function safeBool(v: unknown): boolean {
  return v === true;
}

// Device + environment fingerprint. Fire once per game start.
export const captureDeviceFingerprint = (gameSessionId: string): void => {
  const nr = getNewRelic();
  if (!nr) return;

  const nav: any = window.navigator || {};
  const scr = window.screen || ({} as Screen);
  const connection: any = nav.connection || nav.mozConnection || nav.webkitConnection || {};
  const chrome: any = (window as any).chrome || {};

  let languages = '';
  try {
    languages = Array.isArray(nav.languages) ? nav.languages.join(',') : '';
  } catch (e) {
    /* noop */
  }

  let pluginsLength = 0;
  let mimeTypesLength = 0;
  try {
    pluginsLength = nav.plugins ? nav.plugins.length : 0;
    mimeTypesLength = nav.mimeTypes ? nav.mimeTypes.length : 0;
  } catch (e) {
    /* noop */
  }

  const payload: PageActionPayload = {
    gameSessionId,
    // Automation tells
    webdriver: safeBool(nav.webdriver),
    pluginsLength,
    mimeTypesLength,
    chromePresent: typeof (window as any).chrome !== 'undefined',
    chromeRuntimePresent: typeof chrome.runtime !== 'undefined',
    // Screen + viewport
    screenW: safeNum(scr.width),
    screenH: safeNum(scr.height),
    availW: safeNum(scr.availWidth),
    availH: safeNum(scr.availHeight),
    colorDepth: safeNum(scr.colorDepth),
    devicePixelRatio: safeNum(window.devicePixelRatio),
    viewportW: safeNum(window.innerWidth),
    viewportH: safeNum(window.innerHeight),
    outerW: safeNum(window.outerWidth),
    outerH: safeNum(window.outerHeight),
    // Hardware claims
    hardwareConcurrency: safeNum(nav.hardwareConcurrency),
    deviceMemory: safeNum(nav.deviceMemory),
    maxTouchPoints: safeNum(nav.maxTouchPoints),
    // Locale claims
    platform: safeStr(nav.platform),
    language: safeStr(nav.language),
    languages,
    timezoneIANA: safeStr(Intl.DateTimeFormat().resolvedOptions().timeZone),
    timezoneOffsetMin: new Date().getTimezoneOffset(),
    // Connection hints (only present on some browsers)
    connEffectiveType: safeStr(connection.effectiveType),
    connDownlink: safeNum(connection.downlink),
    connRtt: safeNum(connection.rtt),
    // Visibility at game start
    visibilityState: safeStr((document as any).visibilityState, 'unknown'),
    hasFocusAtStart: typeof document.hasFocus === 'function' ? document.hasFocus() : false,
    // Orientation
    orientationType: safeStr((scr as any).orientation?.type),
  };

  fingerprintSnapshot = {
    webdriver: safeBool(nav.webdriver),
    connRtt: safeNum(connection.rtt),
    pluginsLength,
    mimeTypesLength,
  };

  nr.addPageAction('gameDeviceFingerprint', payload);
};

// Call at game start. Resets in-game counters and attaches listeners.
export const beginGameTelemetry = (): void => {
  jumpTimestamps = [];
  visibilityChangeCount = 0;
  focusChangeCount = 0;
  clickOutOfBoundsCount = 0;
  subHumanReactionCount = 0;
  mouseMoveCount = 0;
  movesSinceLastJump = 0;
  movesBetweenJumpsAccum = [];
  clickBirdDeltaAccum = [];
  primaryPointerType = 'unknown';

  visibilityHandler = () => {
    visibilityChangeCount += 1;
  };
  focusHandler = () => {
    focusChangeCount += 1;
  };
  blurHandler = () => {
    focusChangeCount += 1;
  };

  document.addEventListener('visibilitychange', visibilityHandler);
  window.addEventListener('focus', focusHandler);
  window.addEventListener('blur', blurHandler);

  // Attach pointermove listener to the game container.
  // Bots using page.evaluate() to call jumpBird never generate DOM pointermove.
  if (mouseMoveHandler && mouseMoveTarget) {
    mouseMoveTarget.removeEventListener('pointermove', mouseMoveHandler);
  }
  const container = document.getElementById('relibirds');
  if (container) {
    mouseMoveTarget = container;
    mouseMoveHandler = () => {
      mouseMoveCount += 1;
      movesSinceLastJump += 1;
    };
    container.addEventListener('pointermove', mouseMoveHandler);
  }
};

// Extracts pointer details and records jump timestamp. Returns attributes
// ready to spread into the jumpBird PageAction payload.
export const recordJump = (event?: PointerLike | null, birdY?: number): PageActionPayload => {
  const now = Date.now();
  const prev = jumpTimestamps.length > 0 ? jumpTimestamps[jumpTimestamps.length - 1] : -1;
  jumpTimestamps.push(now);

  const sincePrev = prev === -1 ? -1 : now - prev;

  // Capture primary pointer type from the first jump
  if (primaryPointerType === 'unknown' && event?.pointerType) {
    primaryPointerType = event.pointerType;
  }

  // Sub-human reaction: clicks faster than 50ms apart
  if (sincePrev > 0 && sincePrev < 50) {
    subHumanReactionCount += 1;
  }

  // Click outside viewport bounds (physically impossible for a human)
  const cx = safeNum(event?.clientX);
  const cy = safeNum(event?.clientY);
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  if ((cx > 0 && cy > 0) && (cx > vw || cy > vh || cx < 0 || cy < 0)) {
    clickOutOfBoundsCount += 1;
  }

  // Mousemove-between-jumps accumulation
  movesBetweenJumpsAccum.push(movesSinceLastJump);
  movesSinceLastJump = 0;

  // Pointer-bird spatial coherence (adjust for canvas offset so both are in same coordinate space)
  if (typeof birdY === 'number' && cy > 0) {
    const canvas = document.getElementById('relibirds');
    const canvasTop = canvas ? canvas.getBoundingClientRect().top : 0;
    clickBirdDeltaAccum.push(Math.abs(cy - (canvasTop + birdY)));
  }

  return {
    pointerType: safeStr(event?.pointerType, 'unknown'),
    clientX: cx,
    clientY: cy,
    pressure: safeNum(event?.pressure),
    tiltX: safeNum(event?.tiltX),
    tiltY: safeNum(event?.tiltY),
    isPrimary: safeBool(event?.isPrimary),
    sincePrevJumpMs: sincePrev,
    jumpIndex: jumpTimestamps.length,
  };
};

// Compute mean/stddev/min/max of an array of numbers. Returns 0s for empty.
function stats(arr: number[]): { mean: number; stddev: number; min: number; max: number; count: number } {
  const count = arr.length;
  if (count === 0) return { mean: 0, stddev: 0, min: 0, max: 0, count: 0 };
  let sum = 0;
  let min = Infinity;
  let max = -Infinity;
  for (const v of arr) {
    sum += v;
    if (v < min) min = v;
    if (v > max) max = v;
  }
  const mean = sum / count;
  let sqSum = 0;
  for (const v of arr) sqSum += (v - mean) * (v - mean);
  const stddev = count > 1 ? Math.sqrt(sqSum / (count - 1)) : 0;
  return { mean, stddev, min, max, count };
}

// Sample skewness (Fisher's). Requires n >= 3.
function skewness(arr: number[], mean: number, stddev: number): number {
  const n = arr.length;
  if (n < 3 || stddev === 0) return 0;
  let m3 = 0;
  for (let i = 0; i < n; i += 1) m3 += (arr[i] - mean) ** 3;
  return (n / ((n - 1) * (n - 2))) * (m3 / stddev ** 3);
}

// Call at game end. Fires the gameSummary PageAction and detaches listeners.
export const endGameTelemetry = (ctx: {
  gameSessionId: string | null;
  score: number;
  gameStartTime: number;
  gameEndTime: number;
  playerName: string | null;
  event: string | null;
}): void => {
  const intervals: number[] = [];
  for (let i = 1; i < jumpTimestamps.length; i += 1) {
    intervals.push(jumpTimestamps[i] - jumpTimestamps[i - 1]);
  }
  const iv = stats(intervals);

  const durationMs = ctx.gameEndTime - ctx.gameStartTime;
  const jumpCount = jumpTimestamps.length;
  const jumpsPerScore = ctx.score > 0 ? jumpCount / ctx.score : 0;

  // Distribution shape — humans are right-skewed, bots are symmetric
  const jumpIntervalSkewness = skewness(intervals, iv.mean, iv.stddev);
  const jumpIntervalCv = iv.mean > 0 ? iv.stddev / iv.mean : 0;

  // Mousemove stats
  const avgMovesBetweenJumps = movesBetweenJumpsAccum.length > 0
    ? movesBetweenJumpsAccum.reduce((a, b) => a + b, 0) / movesBetweenJumpsAccum.length
    : 0;

  // Pointer-bird spatial coherence
  const clickBirdYDeltaMean = clickBirdDeltaAccum.length > 0
    ? clickBirdDeltaAccum.reduce((a, b) => a + b, 0) / clickBirdDeltaAccum.length
    : -1;

  const payload: PageActionPayload = {
    gameSessionId: ctx.gameSessionId,
    playerName: ctx.playerName,
    event: ctx.event,
    score: ctx.score,
    durationMs,
    jumpCount,
    jumpsPerScore,
    jumpIntervalMeanMs: Math.round(iv.mean),
    jumpIntervalStddevMs: Math.round(iv.stddev),
    jumpIntervalMinMs: iv.min === 0 ? 0 : Math.round(iv.min),
    jumpIntervalMaxMs: iv.max === 0 ? 0 : Math.round(iv.max),
    visibilityChangeCount,
    focusChangeCount,
    // Bot-detection derived signals
    clickOutOfBoundsCount,
    subHumanReactionCount,
    // Mousemove signals (0 = no DOM pointer activity = likely page.evaluate bot)
    mouseMoveCount,
    avgMovesBetweenJumps: Math.round(avgMovesBetweenJumps * 10) / 10,
    // Timing distribution shape
    jumpIntervalCv: Math.round(jumpIntervalCv * 1000) / 1000,
    jumpIntervalSkewness: Math.round(jumpIntervalSkewness * 1000) / 1000,
    // Pointer-bird spatial coherence
    clickBirdYDeltaMean: Math.round(clickBirdYDeltaMean),
    // Input method (touch users legitimately have 0 pointermove)
    primaryPointerType,
    // Fingerprint echo (avoids cross-event JOIN in NRQL)
    webdriver: fingerprintSnapshot.webdriver,
    connRtt: fingerprintSnapshot.connRtt,
    pluginsLength: fingerprintSnapshot.pluginsLength,
    mimeTypesLength: fingerprintSnapshot.mimeTypesLength,
    timezoneIANA: Intl.DateTimeFormat().resolvedOptions().timeZone,
  };

  // Send via NR Browser agent (may be blocked by ad blockers)
  const nr = getNewRelic();
  if (nr) {
    nr.addPageAction('gameSummary', payload);
  }

  // Always send via our own backend (ad-blocker-proof).
  // Backend records as NR custom event server-side.
  try {
    const url = `${window.location.protocol}//${backendHost()}/gameTelemetry`;
    fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      keepalive: true,
    }).catch(() => { /* fire-and-forget */ });
  } catch (e) { /* noop */ }

  // Detach all listeners
  if (visibilityHandler) {
    document.removeEventListener('visibilitychange', visibilityHandler);
    visibilityHandler = null;
  }
  if (focusHandler) {
    window.removeEventListener('focus', focusHandler);
    focusHandler = null;
  }
  if (blurHandler) {
    window.removeEventListener('blur', blurHandler);
    blurHandler = null;
  }
  if (mouseMoveHandler && mouseMoveTarget) {
    mouseMoveTarget.removeEventListener('pointermove', mouseMoveHandler);
    mouseMoveHandler = null;
    mouseMoveTarget = null;
  }
};

// ---- Server-side game session registration ---------------------------------
// Fire-and-forget HTTP calls that let the backend maintain its own authoritative
// view of the game. Surfaces as sessionServerKnown / serverSideScore / clientServerScoreDelta
// attributes on saveScoreTracking for the confidence score. Never block gameplay on these.

function backendHost(): string {
  const { hostname } = window.location;
  if (hostname.includes('relibirds.newrelic-es.com')) {
    return 'api.relibirds.newrelic-es.com';
  }
  return `${hostname}:3333`;
}

export const registerGameSessionWithServer = (gameSessionId: string): void => {
  try {
    const playerName = localStorage.getItem('playerName') || 'anonymous';
    const url = `${window.location.protocol}//${backendHost()}/gameSession`;
    fetch(url, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ name: playerName, gameSessionId }),
      keepalive: true,
    }).catch(() => {
      /* fire-and-forget; a failure surfaces as sessionServerKnown=false on saveScore */
    });
  } catch (err) {
    /* noop */
  }
};

export const incrementServerSideScore = (gameSessionId: string | null): void => {
  if (!gameSessionId) return;
  try {
    const url = `${window.location.protocol}//${backendHost()}/gameSession`;
    fetch(url, {
      method: 'PUT',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ gameSessionId }),
      keepalive: true,
    }).catch(() => {
      /* fire-and-forget; a miss surfaces as clientServerScoreDelta > 0 on saveScore */
    });
  } catch (err) {
    /* noop */
  }
};
